import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const QuoteCartContext = createContext(null);

const STORAGE_KEY = "cw_quote_cart_v1";

export function QuoteCartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.error("Failed to restore quote cart from storage", e);
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((entry) => {
    setItems((prev) => {
      const key = `${entry.product_id}::${entry.size_label}`;
      const idx = prev.findIndex((i) => `${i.product_id}::${i.size_label}` === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + (entry.quantity || 1) };
        return next;
      }
      return [...prev, { ...entry, quantity: entry.quantity || 1 }];
    });
    setOpen(true);
  }, []);

  const setQuantity = useCallback((product_id, size_label, quantity) => {
    setItems((prev) => prev.map((i) =>
      i.product_id === product_id && i.size_label === size_label
        ? { ...i, quantity: Math.max(1, quantity) }
        : i
    ));
  }, []);

  const removeItem = useCallback((product_id, size_label) => {
    setItems((prev) => prev.filter((i) => !(i.product_id === product_id && i.size_label === size_label)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totalQty = items.reduce((s, i) => s + i.quantity, 0);

  const value = useMemo(() => ({
    items, addItem, setQuantity, removeItem, clear, open, setOpen, totalQty,
  }), [items, addItem, setQuantity, removeItem, clear, open, totalQty]);

  return (
    <QuoteCartContext.Provider value={value}>
      {children}
    </QuoteCartContext.Provider>
  );
}

export function useQuoteCart() {
  const ctx = useContext(QuoteCartContext);
  if (!ctx) throw new Error("useQuoteCart must be used inside QuoteCartProvider");
  return ctx;
}
